import {Backdrop, Box, Grid, LinearProgress, Stack, Typography} from '@mui/material';
import {DataGridPro} from '@mui/x-data-grid-pro';
import LFCButton from '_components/inputs/LFCButton';
import ProgressBar from '_components/ProgressBar';
import GenericTemplate from '_templates/GenericTemplate';

import {AlertHistoryDialog} from './components/AlertHistoryDialog';

import {useAlertSettings} from './hooks/useAlertSettings';
import {AlertSettingsWizardDialog} from './components/AlertSettingsWizardDialog';
import {AlertSettingsDetailDialog} from './components/AlertSettingsDetailDialog';
import {NotificationSettingsDialog} from './components/NotificationSettingsDialog';

export const AlertSettingsPage = () => {
  const {
    t,
    startProcess,
    formRef,
    openAlertHistoryDialog,
    closeAlertHistoryDialog,
    openAlertSettingDialog,
    closeAlertSettingDialog,
    openNotificationDialog,
    closeNotificationDialog,
    isAlertHistoryDlgOpen,
    isAlertSettingDlgOpen,
    isNotificationDlgOpen,
    apiRef,
    dgColumns,
    dgRows,
    alertId,
    notificationAlertId,
    notificationAlertName,
    reload,
    devices,
    emails
  } = useAlertSettings();

  // 新規作成後に通知先設定を開く
  const handleSaveAndOpenNotification = (newAlertId: string) => {
    // 新規作成ダイアログを閉じる
    closeAlertSettingDialog();

    // 少し遅延させて通知先設定ダイアログを開く
    setTimeout(() => {
      // 新しく作成されたアラートの名前を取得
      const newAlert = dgRows.find(row => String(row.id) === newAlertId);
      const alertName = newAlert?.alert_name || '';
      openNotificationDialog(newAlertId, alertName);
    }, 300);
  };

  return (
    <GenericTemplate title={t('menu.page_name.アラート設定')}>
      {startProcess && (
        <Backdrop sx={{zIndex: 9999}} open={true}>
          <Stack sx={{width: '50%'}} spacing={2} textAlign={'center'}>
            <Typography variant="h4" color="secondary">
              Loading..
            </Typography>
            <LinearProgress color="secondary" />
          </Stack>
        </Backdrop>
      )}
      <Typography variant="subtitle2" marginBottom="20px">
        {t('message.データ監視及び通知の方法を設定します。')}
      </Typography>
      <form ref={formRef}>
        <Grid container rowSpacing={2} alignItems="flex-start" justifyContent="flex-start">
          <Grid item xs={2}>
            <Typography variant="h6" fontWeight="bold">
              {t('監視アイテム一覧')}
            </Typography>
          </Grid>
          <Grid item xs={10} textAlign="right" display="flex" justifyContent="flex-end" gap={2}>
            <LFCButton
              color="primary"
              onClick={() => {
                openAlertSettingDialog('-1');
              }}
            >
              {t('監視の新規追加')}
            </LFCButton>
            <LFCButton color="secondary" onClick={openAlertHistoryDialog}>
              {t('監視の実施履歴')}
            </LFCButton>
          </Grid>
          <Grid item xs={12}>
            <Box style={{height: '75vh'}}>
              <DataGridPro
                apiRef={apiRef}
                columns={dgColumns}
                rows={dgRows}
                density={'compact'}
                showCellRightBorder
                showColumnRightBorder
                disableSelectionOnClick
              />
            </Box>
          </Grid>
        </Grid>
      </form>
      <AlertHistoryDialog open={isAlertHistoryDlgOpen} onClose={closeAlertHistoryDialog} />

      {/* 新規作成時: 基本設定のみ */}
      {alertId === '-1' && (
        <AlertSettingsWizardDialog
          open={isAlertSettingDlgOpen}
          onClose={closeAlertSettingDialog}
          alertId={alertId}
          reload={reload}
          devices={devices}
          emails={emails}
          onSaveAndOpenNotification={handleSaveAndOpenNotification}
        />
      )}

      {/* 編集時: 詳細設定のみ（タブなし） */}
      {alertId !== '-1' && (
        <AlertSettingsDetailDialog
          open={isAlertSettingDlgOpen}
          onClose={closeAlertSettingDialog}
          alertId={alertId}
          reload={reload}
          devices={devices}
          emails={emails}
        />
      )}

      {/* 通知先設定ダイアログ（完全独立） */}
      <NotificationSettingsDialog
        open={isNotificationDlgOpen}
        onClose={closeNotificationDialog}
        alertId={notificationAlertId}
        alertName={notificationAlertName}
        emails={emails}
        reload={reload}
      />
    </GenericTemplate>
  );
};
